// src/components/auth/Register.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { registerUser } from '../../features/auth/authSlice';
import { FaChartPie, FaTasks, FaClock, FaUsers } from 'react-icons/fa';
import './Register.css'; // We'll create this CSS file

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedPlan = queryParams.get('plan') || '';

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        company: '',
        jobTitle: '',
        agreeToTerms: false
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });

        // Clear error when field is edited
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        if (!formData.agreeToTerms) {
            newErrors.agreeToTerms = 'You must agree to the terms and conditions';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            // Include selected plan with registration data if available
            const userData = {
                ...formData,
                plan: selectedPlan || 'starter'
            };

            await dispatch(registerUser(userData)).unwrap();
            navigate('/verify-email');
        } catch (error) {
            if (error.field) {
                setErrors({
                    ...errors,
                    [error.field]: error.message
                });
            } else {
                setErrors({
                    ...errors,
                    general: error.message || 'Registration failed. Please try again.'
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="register-container">
            <div className="register-header">
                <button
                    className="home-button"
                    onClick={() => navigate('/')}
                >
                    ← Go to Home
                </button>
            </div>

            <div className="register-content">
                <div className="register-form-container">
                    <div className="form-header">
                        <h1>Create Your Account</h1>
                        <p>Join thousands of teams using AI to prioritize work</p>
                    </div>

                    {selectedPlan && (
                        <div className="selected-plan-badge">
                            <span className="badge-text">Selected Plan: </span>
                            <span className="plan-name">{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}</span>
                        </div>
                    )}

                    {errors.general && <div className="error-message general-error">{errors.general}</div>}

                    <form onSubmit={handleSubmit} className="register-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="firstName">First Name</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className={errors.firstName ? 'input-error' : ''}
                                    placeholder="Enter your first name"
                                />
                                {errors.firstName && <div className="error-text">{errors.firstName}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="lastName">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className={errors.lastName ? 'input-error' : ''}
                                    placeholder="Enter your last name"
                                />
                                {errors.lastName && <div className="error-text">{errors.lastName}</div>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={errors.email ? 'input-error' : ''}
                                placeholder="Enter your email address"
                            />
                            {errors.email && <div className="error-text">{errors.email}</div>}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={errors.password ? 'input-error' : ''}
                                    placeholder="Create a password"
                                />
                                {errors.password && <div className="error-text">{errors.password}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className={errors.confirmPassword ? 'input-error' : ''}
                                    placeholder="Confirm your password"
                                />
                                {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="company">Company (Optional)</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    placeholder="Enter your company name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="jobTitle">Job Title (Optional)</label>
                                <input
                                    type="text"
                                    id="jobTitle"
                                    name="jobTitle"
                                    value={formData.jobTitle}
                                    onChange={handleChange}
                                    placeholder="Enter your job title"
                                />
                            </div>
                        </div>

                        <div className="form-group checkbox-group">
                            <div className="checkbox-container">
                                <input
                                    type="checkbox"
                                    id="agreeToTerms"
                                    name="agreeToTerms"
                                    checked={formData.agreeToTerms}
                                    onChange={handleChange}
                                    className={errors.agreeToTerms ? 'input-error' : ''}
                                />
                                <label htmlFor="agreeToTerms" className="terms-label">
                                    I agree to the <Link to="/terms" target="_blank" className="terms-link">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="terms-link">Privacy Policy</Link>
                                </label>
                            </div>
                            {errors.agreeToTerms && <div className="error-text">{errors.agreeToTerms}</div>}
                        </div>

                        <button
                            type="submit"
                            className="register-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner"></span>
                                    Creating Account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    <div className="register-links">
                        <p>Already have an account? <Link to="/login" className="auth-link">Sign In</Link></p>
                    </div>
                </div>

                <div className="register-benefits">
                    <div className="benefits-container">
                        <h2>Why Join Us?</h2>

                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <FaChartPie />
                            </div>
                            <div className="benefit-content">
                                <h3>Data-Driven Decisions</h3>
                                <p>Make smarter project decisions based on AI analysis.</p>
                            </div>
                        </div>

                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <FaTasks />
                            </div>
                            <div className="benefit-content">
                                <h3>Smart Prioritization</h3>
                                <p>Focus on what matters most with intelligent task prioritization.</p>
                            </div>
                        </div>

                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <FaClock />
                            </div>
                            <div className="benefit-content">
                                <h3>Save Time</h3>
                                <p>Automated analysis of your project backlog saves you hours.</p>
                            </div>
                        </div>

                        <div className="benefit-item">
                            <div className="benefit-icon">
                                <FaUsers />
                            </div>
                            <div className="benefit-content">
                                <h3>Team Collaboration</h3>
                                <p>Work seamlessly with your team on prioritized tasks.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;